// What Survey needs from the app that hosts it (#216), handed over once at
// bootstrap — the same pattern as core's registerHttpClients and
// datacentral-react's configureDataHooks. The host calls createSurveyModule
// from src/app/register-survey.tsx; nothing here imports the app.
//
// Survey opens no modals and reads no employee, so unlike Dayoffs and Business
// it does not extend the shell's HostModalApi / HostSession. It reads the
// permission list (create / export buttons), which the shell contract does not
// cover. Every member is typed structurally, so the host's real objects are
// checked where it registers them, not here.
import type { ComponentType, ReactNode } from 'react'
import type { Control } from 'react-hook-form'
import type { Permission } from '@vnetwork/datacentral-core/auth'
import type { HostLinks } from '@vnetwork/datacentral-shell/host'
import vi from './locales/vi.json'
import en from './locales/en.json'

/** The props Survey gives the host's employee select (filters). */
export interface SurveyEmployeeSelectProps {
    control: Control<any>
    name: string
    multiple?: boolean
    showLabel?: boolean
    label?: string
    departmentIds?: number[]
}

/** The props Survey gives the host's customer select (manual complaint form). */
export interface SurveyCustomerSelectProps {
    control: Control<any>
    name: string
    label?: string
}

/** The props Survey gives the host's masked-value field (phone numbers). */
export interface SurveyPrivacyFieldProps {
    value: string
    type?: 'uuid' | 'email' | 'phone' | 'custom'
    toggleable?: boolean
}

export interface SurveyHost {
    /**
     * The app's i18next instance, already initialised. The `surveys`
     * namespace is registered against it here. A package must not import
     * i18next itself to add bundles: that could hit an instance before the
     * app's init (#59 hazard 5).
     */
    i18n: {
        addResourceBundle: (
            lng: string,
            ns: string,
            resources: object
        ) => unknown
    }
    /**
     * Pages of OTHER modules Survey links to, with a `:id` placeholder. They
     * were `paths.customerDetail` of the app-wide `paths` object; the complaint
     * tab also links to the ticket it opened (`ticketDetail`).
     */
    links: HostLinks<'customerDetail' | 'ticketDetail'>
    /**
     * The signed-in user's permissions, read at render time: the Create and
     * Export buttons are shown for an admin or a user holding the `surveys`
     * resource's `create` / `index` scope.
     */
    usePermissions: () => {
        permissions: readonly Permission[]
        isAdmin: boolean
    }
    /** The app's employee select (surveyor and follower filters). */
    EmployeeSelect: ComponentType<SurveyEmployeeSelectProps>
    /** The app's customer select (manual complaint form). */
    CustomerSelect: ComponentType<SurveyCustomerSelectProps>
    /** The app's masked-value field (customer phone numbers). */
    PrivacyField: ComponentType<SurveyPrivacyFieldProps>
    /** The app's error boundary (wraps the quarterly report's charts). */
    ErrorBoundary: ComponentType<{ children: ReactNode }>
    /** The app's active-filter counter for a filter popover's custom fields. */
    useActiveFilterCount: (control: Control<any>, names: string[]) => number
}

let current: SurveyHost | undefined

export function registerSurveyHost(host: SurveyHost): void {
    current = host
    host.i18n.addResourceBundle('vi', 'surveys', vi)
    host.i18n.addResourceBundle('en', 'surveys', en)
}

/** The registered host. Read it at render or call time, never at import. */
export function getSurveyHost(): SurveyHost {
    if (!current)
        throw new Error(
            '[survey] no host registered. Call createSurveyModule({ i18n, links, … }) at app bootstrap (src/app/register-survey.tsx).'
        )
    return current
}
